import React, { useRef, useState, useEffect, useCallback } from "react";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const PREVIEW_URL = "http://localhost:3000/preview";
const DASHBOARD_ORIGIN = "http://localhost:3001";

/**
 * PreviewPanel
 *
 * Props:
 *  form   {object}  — live form state from PageBuilder
 */
const PreviewPanel = ({ form }) => {
  const iframeRef = useRef(null);
  const [iframeReady, setIframeReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const debounceRef = useRef(null);

  // Send form data to iframe
  const sendUpdate = useCallback((formData) => {
    if (!iframeRef.current?.contentWindow || !iframeReady) return;
    iframeRef.current.contentWindow.postMessage(
      { type: "PREVIEW_UPDATE", payload: formData },
      "http://localhost:3000"
    );
  }, [iframeReady]);

  // Listen for PREVIEW_READY from iframe
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== "http://localhost:3000") return;
      if (event.data?.type === "PREVIEW_READY") {
        setIframeReady(true);
        // Send current form immediately on ready
        sendUpdate(form);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [form, sendUpdate]);

  // Debounced send on every form change
  useEffect(() => {
    if (!iframeReady) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      sendUpdate(form);
    }, 400); // 400ms debounce — smooth but not laggy

    return () => clearTimeout(debounceRef.current);
  }, [form, iframeReady, sendUpdate]);

  const handleRefresh = () => {
    setIframeReady(false);
    setLoading(true);
    if (iframeRef.current) {
      iframeRef.current.src = PREVIEW_URL;
    }
  };

  return (
    <Box
      sx={{
        width: "45%",
        flexShrink: 0,
        borderLeft: "1px solid rgba(255,255,255,0.08)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Panel header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          flexShrink: 0,
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          {/* Live indicator dot */}
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: iframeReady ? "#4ade80" : "#f59e0b",
              boxShadow: iframeReady
                ? "0 0 6px rgba(74,222,128,0.5)"
                : "0 0 6px rgba(245,158,11,0.5)",
              transition: "background 0.3s, box-shadow 0.3s",
            }}
          />
          <Typography variant="caption" color="text.secondary" fontSize="11px">
            {iframeReady ? "Live Preview" : "Connecting…"}
          </Typography>
        </Box>

        <Box display="flex" gap={0.5}>
          <Tooltip title="Refresh preview">
            <IconButton size="small" onClick={handleRefresh}>
              <RefreshIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Open in new tab">
            <IconButton
              size="small"
              onClick={() => window.open(PREVIEW_URL, "_blank")}
            >
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Iframe */}
      <Box sx={{ flex: 1, position: "relative", overflow: "hidden" }}>
        {/* Loading overlay */}
        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: "#0f0f0f",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2,
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                border: "2px solid rgba(255,255,255,0.1)",
                borderTop: "2px solid #3b82f6",
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
                "@keyframes spin": { to: { transform: "rotate(360deg)" } },
              }}
            />
            <Typography variant="caption" color="text.secondary" fontSize="11px">
              Loading preview…
            </Typography>
          </Box>
        )}

        <iframe
          ref={iframeRef}
          src={PREVIEW_URL}
          title="Page Preview"
          onLoad={() => setLoading(false)}
          style={{
            width: "100%",
            height: "100%",
            border: "none",
            display: "block",
          }}
        />
      </Box>
    </Box>
  );
};

export default PreviewPanel;